import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Certificate, Users, MagnifyingGlass } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Certifications = () => {
  const navigate = useNavigate();
  const [certifications, setCertifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchCertifications();
  }, []);

  const fetchCertifications = async () => {
    try {
      const response = await axios.get(`${API}/certifications`);
      setCertifications(response.data);
    } catch (error) {
      console.error('Error fetching certifications:', error);
      toast.error('Failed to load certifications');
    } finally {
      setLoading(false);
    }
  };

  const searchLc = search.trim().toLowerCase();
  const filtered = searchLc
    ? certifications.filter((c) => c.name.toLowerCase().includes(searchLc))
    : certifications;

  if (loading) {
    return (
      <div className="pt-24 pb-12 px-6 sm:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-24 bg-white border border-slate-200 rounded-lg skeleton" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-12 px-6 sm:px-8 lg:px-12" data-testid="certifications-page">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl font-medium tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Certifications
          </h1>
          <p className="text-base font-normal text-slate-600 leading-relaxed mt-2">
            All certifications found across candidate profiles
          </p>
        </div>

        <div className="relative mb-6">
          <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search certifications"
            className="w-full pl-11 pr-4 py-2.5 bg-white border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
            data-testid="certifications-search-input"
          />
        </div>

        {certifications.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg p-12 text-center" data-testid="empty-certifications-state">
            <div className="inline-flex bg-blue-50 p-4 rounded-lg mb-4">
              <Certificate size={48} weight="duotone" className="text-[#002FA7]" />
            </div>
            <p className="text-lg font-medium text-slate-800 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              No certifications found yet
            </p>
            <p className="text-base text-slate-600">
              Certifications extracted from uploaded CVs will show up here.
            </p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg p-12 text-center" data-testid="no-matching-certifications">
            <p className="text-base text-slate-600">No certifications match "{search}"</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="certifications-grid">
            {filtered.map((cert) => (
              <button
                key={cert.name}
                onClick={() => navigate(`/candidates?certification=${encodeURIComponent(cert.name)}`)}
                className="text-left bg-white border border-slate-200 rounded-lg p-5 hover:shadow-md hover:-translate-y-0.5 hover:border-[#002FA7] transition-all duration-200"
                data-testid={`certification-card-${cert.name}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="bg-blue-50 p-2.5 rounded-md">
                    <Certificate size={20} weight="duotone" className="text-[#002FA7]" />
                  </div>
                  <span className="flex items-center space-x-1 text-xs font-medium text-[#002FA7] bg-blue-50 px-2 py-1 rounded">
                    <Users size={12} weight="bold" />
                    <span>{cert.count}</span>
                  </span>
                </div>
                <h3 className="text-base font-medium text-slate-900 line-clamp-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {cert.name}
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  {cert.count} candidate{cert.count !== 1 ? 's' : ''}
                </p>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Certifications;
